import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import MergeVariableDropdown from '../../MergeVariableDropdown';

/**
 * Text block — inline TipTap editor for rich text within the email canvas.
 * Includes merge variable insertion and mini formatting toolbar.
 */
export default function EmailTextBlock({ props, onChange, isSelected }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: 'Start typing...' }),
    ],
    content: props.content || '',
    onUpdate: ({ editor }) => {
      onChange({ ...props, content: editor.getHTML() });
    },
    editorProps: {
      attributes: { class: 'focus:outline-none' },
    },
  });

  useEffect(() => {
    if (editor && props.content !== editor.getHTML()) {
      editor.commands.setContent(props.content || '', false);
    }
  }, [props.content]);

  const handleInsertVariable = useCallback((variable) => {
    if (!editor) return;
    editor.chain().focus().insertContent(variable).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div style={{ padding: props.padding || '16px 24px', color: props.text_color || '#1c1917', backgroundColor: props.background_color || 'transparent', fontSize: props.font_size || '14px', lineHeight: props.line_height || '1.6' }}>
      {/* Mini toolbar on selection */}
      {isSelected && (
        <div className="flex items-center gap-0.5 mb-2 p-1 bg-white rounded-lg border border-stone-200 shadow-sm flex-wrap" onClick={e => e.stopPropagation()}>
          <Btn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">B</Btn>
          <Btn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><em>I</em></Btn>
          <Btn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><u>U</u></Btn>
          <Btn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough"><s>S</s></Btn>
          <span className="w-px h-4 bg-stone-200 mx-0.5" />
          <Btn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</Btn>
          <Btn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</Btn>
          <Btn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</Btn>
          <span className="w-px h-4 bg-stone-200 mx-0.5" />
          <Btn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">•</Btn>
          <Btn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">1.</Btn>
          <Btn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">"</Btn>
          <span className="w-px h-4 bg-stone-200 mx-0.5" />
          <Btn active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>L</Btn>
          <Btn active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>C</Btn>
          <Btn active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>R</Btn>
          <span className="w-px h-4 bg-stone-200 mx-0.5" />
          <Btn onClick={() => {
            const url = window.prompt('Link URL:');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }} active={editor.isActive('link')}>Link</Btn>
          <span className="w-px h-4 bg-stone-200 mx-0.5" />
          <MergeVariableDropdown onInsert={handleInsertVariable} />
        </div>
      )}
      <EditorContent editor={editor} className="prose prose-sm max-w-none [&_.ProseMirror]:min-h-[24px]" />
    </div>
  );
}

function Btn({ children, active, onClick, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`px-1.5 py-0.5 text-[11px] font-medium rounded transition-colors ${active ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>
      {children}
    </button>
  );
}
